import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { getUserToken } from '@/redux/user';
import {
    useLazyGetBrandPaymentConfigurationQuery,
    useUpdateBrandPaymentConfigurationMutation,
    useCountPostPaymentCustomersQuery,
} from '@/services/innovorder/brand/brand.endpoints';

interface ParamTypes {
    brandId: string;
}

interface InfoViewModel {
    isLoading: boolean;
    isSaving: boolean;
    useEwalletOverdraft: boolean;
    ewalletOverdraftAmount: number;
    postPaymentCustomersCount: number;
    canDisableOverdraft: boolean;
    changesCount: number;
    handleToggleOverdraft: (value: boolean) => void;
    handleAmountChange: (value: number) => void;
    handleSave: () => Promise<void>;
    handleCancel: () => void;
}

const DEFAULT_OVERDRAFT_AMOUNT = 1; // 0.01 in currency (stored in cents)

const useInfoVM = (): InfoViewModel => {
    const token = useSelector(getUserToken);
    const { brandId } = useParams<ParamTypes>();
    const brandIdNumber = parseInt(brandId, 10);

    const [fetchPaymentConfig, { data: paymentConfig, isLoading: isLoadingConfig }] =
        useLazyGetBrandPaymentConfigurationQuery();
    const [updatePaymentConfig, { isLoading: isSaving }] = useUpdateBrandPaymentConfigurationMutation();

    const { data: postPaymentData, isLoading: isLoadingPostPayment } = useCountPostPaymentCustomersQuery(
        { brandId: brandIdNumber, token },
        { skip: !token },
    );

    const [useEwalletOverdraft, setUseEwalletOverdraft] = useState<boolean>(false);
    const [ewalletOverdraftAmount, setEwalletOverdraftAmount] = useState<number>(0);

    const postPaymentCustomersCount = postPaymentData?.count ?? 0;

    // Can only disable overdraft if there are no post-payment customers
    const canDisableOverdraft = postPaymentCustomersCount === 0;

    useEffect(() => {
        if (token) {
            fetchPaymentConfig({ brandId: brandIdNumber, token });
        }
    }, [token, brandIdNumber, fetchPaymentConfig]);

    useEffect(() => {
        if (paymentConfig) {
            setUseEwalletOverdraft(paymentConfig.useEwalletOverdraft);
            setEwalletOverdraftAmount(paymentConfig.ewalletOverdraftAmount);
        }
    }, [paymentConfig]);

    const changesCount = useMemo(() => {
        if (!paymentConfig) return 0;
        let count = 0;
        if (paymentConfig.useEwalletOverdraft !== useEwalletOverdraft) count += 1;
        if (paymentConfig.ewalletOverdraftAmount !== ewalletOverdraftAmount) count += 1;
        return count;
    }, [paymentConfig, useEwalletOverdraft, ewalletOverdraftAmount]);

    const handleToggleOverdraft = useCallback(
        (value: boolean) => {
            setUseEwalletOverdraft(value);
            if (value) {
                // Set default amount when toggling ON
                setEwalletOverdraftAmount(DEFAULT_OVERDRAFT_AMOUNT);
            } else {
                // Reset amount when toggling OFF
                setEwalletOverdraftAmount(0);
            }
        },
        [setUseEwalletOverdraft, setEwalletOverdraftAmount],
    );

    const handleAmountChange = useCallback((value: number) => {
        setEwalletOverdraftAmount(value);
    }, []);

    const handleSave = useCallback(async () => {
        if (!token) return;

        await updatePaymentConfig({
            brandId: brandIdNumber,
            token,
            useEwalletOverdraft,
            ewalletOverdraftAmount,
        });

        // Refetch to sync state
        fetchPaymentConfig({ brandId: brandIdNumber, token });
    }, [token, brandIdNumber, useEwalletOverdraft, ewalletOverdraftAmount, updatePaymentConfig, fetchPaymentConfig]);

    const handleCancel = useCallback(() => {
        if (paymentConfig) {
            setUseEwalletOverdraft(paymentConfig.useEwalletOverdraft);
            setEwalletOverdraftAmount(paymentConfig.ewalletOverdraftAmount);
        }
    }, [paymentConfig]);

    return {
        isLoading: isLoadingConfig || isLoadingPostPayment,
        isSaving,
        useEwalletOverdraft,
        ewalletOverdraftAmount,
        postPaymentCustomersCount,
        canDisableOverdraft,
        changesCount,
        handleToggleOverdraft,
        handleAmountChange,
        handleSave,
        handleCancel,
    };
};

export default useInfoVM;
